/**
 * BFP-1144 — local mirror of `@brushfire/utils` notificationContent (CI clones shared from `dev-10`
 * without these exports). Keep counting/sanitizing semantics identical to campaign-service.
 */

const ALLOWED_TAGS = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a'])
const VOID_TAGS = new Set(['br'])
const DROP_WITH_CONTENT = ['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template']
const SAFE_HREF_PREFIXES = ['https://', 'http://', 'mailto:']
const BLOCK_CLOSE_TAGS = new Set(['p', 'li', 'ul', 'ol'])

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
}

function decodeEntities(text: string): string {
  return text.replaceAll(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
    const lower = entity.toLowerCase()
    if (lower.startsWith('#x')) {
      const code = Number.parseInt(lower.slice(2), 16)
      return Number.isFinite(code) ? String.fromCodePoint(code) : match
    }
    if (lower.startsWith('#')) {
      const code = Number.parseInt(lower.slice(1), 10)
      return Number.isFinite(code) ? String.fromCodePoint(code) : match
    }
    return NAMED_ENTITIES[lower] ?? match
  })
}

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
}

function removeBlocks(html: string, tag: string): string {
  let result = html
  let lower = result.toLowerCase()
  const open = `<${tag}`
  const close = `</${tag}`
  let start = lower.indexOf(open)
  while (start !== -1) {
    const closeStart = lower.indexOf(close, start + open.length)
    let end: number
    if (closeStart === -1) {
      end = result.length
    } else {
      const closeEnd = lower.indexOf('>', closeStart)
      end = closeEnd === -1 ? result.length : closeEnd + 1
    }
    result = result.slice(0, start) + result.slice(end)
    lower = result.toLowerCase()
    start = lower.indexOf(open)
  }
  return result
}

interface ParsedTag {
  name: string
  closing: boolean
  attributes: string
}

function parseTag(inner: string): ParsedTag | null {
  const trimmed = inner.trim()
  const closing = trimmed.startsWith('/')
  const body = closing ? trimmed.slice(1).trimStart() : trimmed
  let nameEnd = 0
  while (nameEnd < body.length && /[a-z0-9]/i.test(body[nameEnd])) nameEnd += 1
  if (nameEnd === 0) return null
  return {
    name: body.slice(0, nameEnd).toLowerCase(),
    closing,
    attributes: body.slice(nameEnd),
  }
}

function readHref(attributes: string): string | null {
  const match = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(attributes)
  if (!match) return null
  const raw = (match[1] ?? match[2] ?? match[3] ?? '').trim()
  const decoded = decodeEntities(raw).trim()
  const lower = decoded.toLowerCase()
  return SAFE_HREF_PREFIXES.some((prefix) => lower.startsWith(prefix)) ? decoded : null
}

function renderAllowedTag(tag: ParsedTag): string {
  if (tag.closing) return VOID_TAGS.has(tag.name) ? '' : `</${tag.name}>`
  if (tag.name !== 'a') return `<${tag.name}>`
  const href = readHref(tag.attributes)
  return href
    ? `<a href="${escapeAttribute(href)}" target="_blank" rel="noopener noreferrer">`
    : '<a>'
}

/** Sanitizes the markup starting at `source[start] === '<'`; returns the emitted text and next index. */
function consumeMarkup(source: string, start: number): { text: string; next: number } {
  if (source.startsWith('<!--', start)) {
    const commentEnd = source.indexOf('-->', start + 4)
    return { text: '', next: commentEnd === -1 ? source.length : commentEnd + 3 }
  }
  const close = source.indexOf('>', start + 1)
  if (close === -1) return { text: '&lt;', next: start + 1 }
  const tag = parseTag(source.slice(start + 1, close))
  const text = tag && ALLOWED_TAGS.has(tag.name) ? renderAllowedTag(tag) : ''
  return { text, next: close + 1 }
}

/**
 * Allowlist sanitizer for rich-text email bodies. Keeps only basic formatting tags
 * (`p br strong b em i u ul ol li a`), strips every attribute except a safe `href`
 * (`http`, `https`, `mailto`) on links, and removes script-like blocks with their content.
 */
export function sanitizeNotificationHtml(html: string): string {
  if (!html) return ''
  let source = html
  for (const tag of DROP_WITH_CONTENT) source = removeBlocks(source, tag)

  let out = ''
  let i = 0
  while (i < source.length) {
    const ch = source[i]
    if (ch === '<') {
      const { text, next } = consumeMarkup(source, i)
      out += text
      i = next
    } else {
      out += ch === '>' ? '&gt;' : ch
      i += 1
    }
  }
  return out.trim()
}

/**
 * Plain text from rich HTML (tags removed, entities decoded, trimmed).
 * With `preserveLineBreaks`, block ends and `<br>` become newlines — for `.txt` email / SMS output.
 */
export function plainTextFromHtml(
  html: string,
  options: { preserveLineBreaks?: boolean } = {},
): string {
  if (!html) return ''
  let out = ''
  let i = 0
  while (i < html.length) {
    if (html[i] !== '<') {
      out += html[i]
      i += 1
      continue
    }
    const close = html.indexOf('>', i + 1)
    if (close === -1) {
      out += html.slice(i)
      break
    }
    if (options.preserveLineBreaks) {
      const tag = parseTag(html.slice(i + 1, close))
      if (tag && (tag.name === 'br' || (tag.closing && BLOCK_CLOSE_TAGS.has(tag.name)))) {
        out += '\n'
      }
    }
    i = close + 1
  }
  const decoded = decodeEntities(out)
  if (!options.preserveLineBreaks) return decoded.trim()
  return decoded
    .split('\n')
    .map((line) => line.trim())
    .filter((line, idx, lines) => line.length > 0 || (idx > 0 && lines[idx - 1].length > 0))
    .join('\n')
    .trim()
}

/** Character count used for every BFP-1144 limit (plain text, markup excluded). */
export function plainTextLength(html: string): number {
  return plainTextFromHtml(html).length
}
